import { useCallback, useMemo } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StudentFilters, StudentProfile } from '@court-sync/shared';
import type { PaginationState } from '@/components/ui';
import { academyStudentService } from '../services/academyStudentService';
import { STALE_TIME, queryClient } from '../queryClient';
import { queryKeys } from './keys';
import { serializeFilters } from './stableFilters';

export interface AcademyStudentStats {
  totalStudents: number;
  averageAttendanceRate: number;
  perfectAttendance: number;
  atRisk: number;
}

export interface AcademyStudentsPageResult {
  students: StudentProfile[];
  pagination: PaginationState | null;
}

export function patchAcademyStudentInListCache(student: StudentProfile): void {
  const listEntries = queryClient.getQueriesData<AcademyStudentsPageResult>({
    queryKey: queryKeys.students.lists(),
  });
  for (const [key, page] of listEntries) {
    if (!page?.students?.some((s) => s.id === student.id)) continue;
    queryClient.setQueryData<AcademyStudentsPageResult>(key, {
      ...page,
      students: page.students.map((s) => (s.id === student.id ? { ...s, ...student } : s)),
    });
  }
}

export function invalidateAcademyStudentsQueries(academyId?: string): void {
  void queryClient.invalidateQueries({
    queryKey: queryKeys.students.lists(),
    ...(academyId ? { predicate: (q) => q.queryKey.includes(academyId) } : {}),
  });
}

export function useAcademyStudentsPage(
  academyId?: string,
  page = 1,
  filters: StudentFilters = {},
  options?: { enabled?: boolean }
) {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;
  const serializedFilters = serializeFilters(filters);
  const stableFilters = useMemo(() => filters, [serializedFilters, filters]);
  const queryKey = queryKeys.students.list(academyId ?? '', page, stableFilters);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<AcademyStudentsPageResult> => {
      const response = await academyStudentService.getAcademyStudentsPaginated(
        academyId!,
        page,
        20,
        stableFilters
      );
      return {
        students: response.data || [],
        pagination: response.pagination || null,
      };
    },
    enabled: !!academyId && enabled,
    staleTime: STALE_TIME.MEDIUM,
    placeholderData: keepPreviousData,
  });

  const refresh = useCallback(
    async (opts?: { force?: boolean }) => {
      if (opts?.force) {
        await queryClient.invalidateQueries({ queryKey });
      }
      await refetch();
    },
    [queryClient, queryKey, refetch]
  );

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.students.lists() });
  }, [queryClient]);

  return {
    students: data?.students ?? [],
    pagination: data?.pagination ?? null,
    isLoading,
    error: error instanceof Error ? error.message : error ? String(error) : null,
    refresh,
    invalidate,
  };
}

export function useAcademyStudentStats(academyId?: string, options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true;
  const queryKey = queryKeys.students.stats(academyId ?? '');

  const { data: stats = null, isLoading, refetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<AcademyStudentStats> => {
      const response = await academyStudentService.getAttendanceStats(academyId!);
      return (
        response.data ?? {
          totalStudents: 0,
          averageAttendanceRate: 0,
          perfectAttendance: 0,
          atRisk: 0,
        }
      );
    },
    enabled: !!academyId && enabled,
    staleTime: STALE_TIME.MEDIUM,
  });

  const queryClient = useQueryClient();
  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.students.stats(academyId ?? '') });
  }, [queryClient, academyId]);

  return {
    stats,
    isLoading,
    refresh: () => refetch(),
    invalidate,
  };
}
